import {Duration} from "luxon";
import type {EnumTranslations, EnumType, TypeOfEnum} from "../../helpers/EnumHelpers";
import {type FieldOrFunctionOf, getValue, isNonOptional, type NonOptional, type Optional} from "../../helpers/TypeHelpers";
import {translateYesNo} from "../../helpers/YesNoTranslation";

export interface SelectOption<T> {
  readonly label: string;
  readonly value: T;
}

export type SelectOptionGroupKey = string | number | boolean;

export interface SelectOptionGroup<T, TKey extends SelectOptionGroupKey> {
  readonly key: TKey;
  readonly label: string;
  readonly options: readonly SelectOption<T>[];
}

export type SelectOptionsElement<T, TGroupKey extends Optional<SelectOptionGroupKey>> =
  SelectOption<T> | SelectOptionGroup<T, NonOptional<TGroupKey>>;
export type SelectOptions<T, TGroupKey extends Optional<SelectOptionGroupKey> = SelectOptionGroupKey> = readonly SelectOptionsElement<T, TGroupKey>[];

export function isSelectOption<T>(value: unknown): value is SelectOption<T> {
  return value !== null && typeof value === "object" && ("label" in value) && ("value" in value) && typeof value.label === "string";
}

export function isSelectOptionGroup<T, TKey extends SelectOptionGroupKey>(value: unknown): value is SelectOptionGroup<T, TKey> {
  return value !== null && typeof value === "object" && ("label" in value) && ("options" in value)
    && Array.isArray(value.options) && typeof value.label === "string";
}

export function idOfSelectOptionsElement<T, TKey extends SelectOptionGroupKey>(element: SelectOptionsElement<T, TKey>): SelectOption<T>["value"] | TKey {
  return isSelectOption(element) ? element.value : element.key;
}

const visitAdditionalTimeOptions: readonly SelectOption<Duration<true>>[] = Array.from({length: 48}).flatMap((_e, i) =>
  Array.from({length: 3}).map((_e, j) => {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const duration = Duration.fromObject({hours: i, minutes: j * 15});
    return {
      value: duration,
      label: duration.toFormat("hh:mm"),
    };
  }),
);

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const pageSizeOptions = [25, 50, 100, 250, 500, 1000];
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const serviceIntervalMonths = [1, 2, 3, 4, 6, 12, 18, 24, 30, 36, 48, 60, 72];
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const paymentIntervalYears = [1, 2, 3, 4, 6, 12];

interface ICommonSelectOptions {
  readonly boolean: readonly SelectOption<boolean>[];
  readonly pageSize: readonly SelectOption<number>[];
  readonly serviceInterval: readonly SelectOption<Optional<number>>[];
  readonly paymentInterval: readonly SelectOption<Optional<number>>[];
  readonly visitAdditionalTime: readonly SelectOption<Duration<true>>[];

  forEnum<TEnumType extends EnumType>(enumType: TEnumType,
                                      translations: EnumTranslations<TypeOfEnum<TEnumType>>,
                                      optionalSelectionOptions?: false | {
                                        readonly addUndefinedAtBeginning: false,
                                      }): readonly SelectOption<TypeOfEnum<TEnumType>>[];

  forEnum<TEnumType extends EnumType>(enumType: TEnumType,
                                      translations: EnumTranslations<TypeOfEnum<TEnumType>>,
                                      optionalSelectionOptions: boolean | {
                                        readonly addUndefinedAtBeginning: boolean,
                                        readonly label?: string,
                                      }): readonly SelectOption<Optional<TypeOfEnum<TEnumType>>>[];
}

export const CommonSelectOptions: ICommonSelectOptions = {
  get boolean(): readonly SelectOption<boolean>[] {
    return [false, true].map(v => ({value: v, label: translateYesNo(v)}));
  },
  get pageSize(): readonly SelectOption<number>[] {
    return pageSizeOptions.map(v => ({
      value: v, label: $localize`:@@pagination.page-size-option:${v}:items_per_page: / Page`,
    }));
  },
  get serviceInterval(): readonly SelectOption<Optional<number>>[] {
    return [
      {value: undefined, label: $localize`:@@service-intervals.no-interval:No Interval`},
      ...serviceIntervalMonths.map(months => ({
        value: months,
        label: months === 1
          ? $localize`:@@service-intervals.x-month:${months}:month: month`
          : $localize`:@@service-intervals.x-months:${months}:months: months`,
      })),
    ];
  },
  get paymentInterval(): readonly SelectOption<Optional<number>>[] {
    return [
      {value: undefined, label: $localize`:@@payment-intervals.no-agreement:No Agreement`},
      ...paymentIntervalYears.map(yearlyInterval => ({
        value: yearlyInterval,
        label: $localize`:@@payment-intervals.x-per-year:${yearlyInterval}:payments_per_year: per Year`,
      })),
    ];
  },
  visitAdditionalTime: visitAdditionalTimeOptions,

  forEnum<TEnumType extends EnumType>(
    enumType: TEnumType,
    translations: EnumTranslations<TypeOfEnum<TEnumType>>,
    optionalSelectionOptions?: boolean | {
      readonly addUndefinedAtBeginning: boolean,
      readonly label?: string,
    }): readonly SelectOption<TypeOfEnum<TEnumType>>[] {
    if (typeof optionalSelectionOptions === "boolean")
      optionalSelectionOptions = {addUndefinedAtBeginning: optionalSelectionOptions};
    // We cannot iterate over the translations here, since the underlying type of the enum (value) is then lost.
    // So number-based enums would result in string values...
    const options = Object.values(enumType)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      .map(value => toSelectOptionForEnum(value as TypeOfEnum<TEnumType>, translations))
      // Number-based enums also have a reverse mapping, which results in values without translation. We filter those out.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      .filter(s => s.label !== undefined);
    if (optionalSelectionOptions?.addUndefinedAtBeginning === true) {
      (options as SelectOption<Optional<TypeOfEnum<TEnumType>>>[]).unshift({
        value: undefined,
        label: optionalSelectionOptions.label ?? "",
      });
    }
    return options;
  },
};

export type SelectOptionLabelProvider<TObject extends object> = FieldOrFunctionOf<TObject, string>;

export interface SelectOptionValueProviders<TObject extends object, TValue> {
  readonly value: FieldOrFunctionOf<TObject, TValue>;
  readonly label: SelectOptionLabelProvider<TObject>;
}

export function toSelectOption<TObject extends object, TValue>(
  model: Readonly<TObject>,
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
): SelectOption<TValue> {
  return {value: getValue(model, valueProviders.value), label: getValue(model, valueProviders.label)};
}

export function toSelectOptionForEnum<TEnumType extends EnumType, TEnum extends TypeOfEnum<TEnumType>>(
  enumCase: TEnum,
  translations: EnumTranslations<TEnum>,
): SelectOption<TEnum> {
  return {value: enumCase, label: translations[enumCase]};
}

type SelectOptionGroupRecordKey<TGroupKey extends Optional<SelectOptionGroupKey>> = TGroupKey extends boolean ? `${TGroupKey}` : NonOptional<TGroupKey>;

export interface SelectOptionsGrouping<TObject extends object, TGroupKey extends Optional<SelectOptionGroupKey>> {
  readonly key: FieldOrFunctionOf<TObject, TGroupKey>;
  readonly label: FieldOrFunctionOf<TObject, string> | Readonly<Record<SelectOptionGroupRecordKey<TGroupKey>, string>>;
}

export function toSelectOptions<TObject extends object, TValue>(
  models: readonly TObject[],
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
): readonly SelectOption<TValue>[];
export function toSelectOptions<TObject extends object, TValue, TGroupKey extends SelectOptionGroupKey>(
  models: readonly TObject[],
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
  grouping: SelectOptionsGrouping<TObject, TGroupKey>,
): readonly SelectOptionGroup<TValue, TGroupKey>[];
export function toSelectOptions<TObject extends object, TValue, TGroupKey extends Optional<SelectOptionGroupKey>>(
  models: readonly TObject[],
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
  grouping?: SelectOptionsGrouping<TObject, TGroupKey>,
): SelectOptions<TValue, TGroupKey>;
export function toSelectOptions<TObject extends object, TValue, TGroupKey extends Optional<SelectOptionGroupKey>>(
  models: readonly TObject[],
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
  grouping?: SelectOptionsGrouping<TObject, TGroupKey>,
): SelectOptions<TValue, TGroupKey> {
  if (grouping === undefined)
    return models.map(model => toSelectOption(model, valueProviders));
  const results: SelectOptionsElement<TValue, TGroupKey>[] = [];
  const groups = new Map<TGroupKey, SelectOptionGroup<TValue, NonOptional<TGroupKey>> & {
    readonly options: SelectOptionsElement<TValue, TGroupKey>[]
  }>();
  for (const model of models) {
    const selectOption = toSelectOption(model, valueProviders);
    const groupKey = getValue(model, grouping.key);
    if (!isNonOptional(groupKey))
      results.push(selectOption);
    else {
      let group = groups.get(groupKey);
      if (group === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const labelKey = (typeof groupKey === "boolean" ? `${groupKey}` as `${TGroupKey}` : groupKey) as SelectOptionGroupRecordKey<TGroupKey>;
        const groupLabel = typeof grouping.label === "object" ? grouping.label[labelKey] : getValue(model, grouping.label);
        group = {key: groupKey, label: groupLabel, options: []};
        groups.set(groupKey, group);
        results.push(group);
      }
      group.options.push(selectOption);
    }
  }
  return results;
}
